import {
  expand, hueForTag, slugifyTag, sortInstances, spanOverlaps,
  type IsoDate, type Tag, type Task, type TaskInstance,
} from '@hestia/core';
import type { Op, QueuedOp } from './ops.js';

/** Pending writes, drawn on top of whatever the server last said.
 *
 *  A queued write is invisible to the server by definition, so a view built only
 *  from cached data would show a task you just created as missing and a box you
 *  just ticked as unticked. Every read therefore replays the queue over the
 *  server's answer before anything is rendered. Ops replay in the order they were
 *  made, so a task created and then completed offline comes out completed.
 *
 *  This is a projection, never a source of truth. The queue drains, the next
 *  fetch returns the real thing, and the overlay for that op disappears with it. */

export interface Ctx {
  /** Who is signed in — a task created here is created by them. */
  meId: string;
  /** Tags the server has told us about, for resolving a typed name to its row. */
  tags: Tag[];
  today: IsoDate;
}

/** Deterministic, and derived the same way the server derives the real one, so a
 *  tag invented offline keeps its colour and its identity when it lands. */
export const localTagId = (name: string) => `local:${slugifyTag(name)}`;

function resolveTagIds(names: string[], tags: Tag[]): string[] {
  return names.map((n) => {
    const slug = slugifyTag(n);
    return tags.find((t) => t.slug === slug)?.id ?? localTagId(n);
  });
}

function taskFromCreate(op: Extract<Op, { kind: 'create' }>, ctx: Ctx): Task {
  const now = new Date(0).toISOString();
  return {
    id: op.taskId,
    title: op.input.title,
    notes: op.input.notes,
    tagIds: resolveTagIds(op.input.tags, ctx.tags),
    assigneeId: op.input.assigneeId,
    createdBy: ctx.meId,
    startDate: op.input.startDate,
    endDate: (op.input as { endDate?: IsoDate | null }).endDate ?? null,
    startTime: op.input.startTime as Task['startTime'],
    durationMin: op.input.durationMin,
    rrule: op.input.rrule,
    remindMinBefore: null,
    createdAt: now,
    // Epoch, not now: this task has never been near the server, so there is no
    // "unmodified since" it could honestly claim. Any edit to it while it is
    // still queued is folded into the create rather than guarded against it.
    updatedAt: now,
  };
}

const instancesFor = (task: Task, from: IsoDate, to: IsoDate, today: IsoDate): TaskInstance[] => {
  // A span is one row on the day it starts, matching what the server would send
  // back — otherwise a task made offline would visibly rearrange on landing.
  if (task.endDate) {
    return spanOverlaps(task.startDate, task.endDate, from, to)
      ? [{ task, date: task.startDate, originalDate: task.startDate, endDate: task.endDate,
           done: false, completedBy: null, completedAt: null,
           stillOpen: task.endDate < today }]
      : [];
  }
  return expand(task.startDate, task.rrule, from, to).map((date) => ({
    task, date, originalDate: date, endDate: null, done: false,
    completedBy: null, completedAt: null, stillOpen: date < today,
  }));
};

function patched(task: Task, patch: Record<string, unknown>, tags: Tag[]): Task {
  const { tags: names, ifUnmodifiedSince: _guard, ...fields } = patch as
    { tags?: string[]; ifUnmodifiedSince?: number } & Record<string, unknown>;
  return {
    ...task,
    ...(fields as Partial<Task>),
    ...(names ? { tagIds: resolveTagIds(names, tags) } : null),
  };
}

/** Replay the queue over one range of days. `from`/`to` bound what a created task
 *  is expanded into — the same window the server was asked for. */
export function overlayInstances(
  server: TaskInstance[], queue: QueuedOp[], from: IsoDate, to: IsoDate, ctx: Ctx,
): TaskInstance[] {
  if (!queue.length) return server;
  let list = server;
  const hits = (i: TaskInstance, taskId: string, date: IsoDate) =>
    i.task.id === taskId && i.originalDate === date;

  for (const { op } of queue) {
    switch (op.kind) {
      case 'create':
        list = [...list, ...instancesFor(taskFromCreate(op, ctx), from, to, ctx.today)];
        break;
      case 'update':
        list = list.map((i) => {
          if (i.task.id !== op.taskId) return i;
          const task = patched(i.task, op.patch, ctx.tags);
          // Moving the start of a one-off moves the day it sits on. A repeating
          // task would need its whole rule re-expanded, which the next fetch will
          // do properly; until then it stays where it is rather than guessing.
          const moved = !task.rrule && task.startDate !== i.task.startDate;
          return moved
            ? { ...i, task, date: task.startDate, originalDate: task.startDate,
                stillOpen: !i.done && task.startDate < ctx.today }
            : { ...i, task };
        });
        break;
      case 'delete':
        list = list.filter((i) => i.task.id !== op.taskId);
        break;
      case 'complete':
        list = list.map((i) => (hits(i, op.taskId, op.date)
          ? { ...i, done: op.done, stillOpen: !op.done && i.date < ctx.today }
          : i));
        break;
      case 'move':
        list = list.map((i) => (hits(i, op.taskId, op.date)
          ? { ...i, date: op.to, stillOpen: !i.done && op.to < ctx.today }
          : i));
        break;
    }
  }
  return list.filter((i) => i.date >= from && i.date <= to).sort(sortInstances);
}

/** Tags typed into a task that has not reached the server yet do not exist there,
 *  so the sidebar would render the pill as a blank. Mint them locally, and count
 *  the pending tasks against tags that do exist so a tag stops looking unused the
 *  moment something offline uses it. */
export function overlayTags(server: Tag[], queue: QueuedOp[]): Tag[] {
  if (!queue.length) return server;
  const byId = new Map(server.map((t) => [t.id, { ...t }]));
  const bump = (names: string[]) => {
    for (const name of names) {
      const slug = slugifyTag(name);
      if (!slug) continue;
      const existing = [...byId.values()].find((t) => t.slug === slug);
      if (existing) { existing.taskCount += 1; continue; }
      const id = localTagId(name);
      const local = byId.get(id);
      if (local) local.taskCount += 1;
      else byId.set(id, {
        id, slug, name: name.replace(/^#+/, '').trim() || slug,
        hue: hueForTag(slug), taskCount: 1,
      });
    }
  };
  for (const { op } of queue) {
    if (op.kind === 'create') bump(op.input.tags);
    else if (op.kind === 'update' && Array.isArray(op.patch.tags)) bump(op.patch.tags as string[]);
  }
  return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
}
